import type {
  GltfLoadingOptions,
  GltfLoadingOptionsInput,
  GltfLowMemoryReasonCode,
  GltfMemoryBudgetOptions,
} from '@/plugins/mesh/types'

const MEBIBYTE = 1024 * 1024
export const MAX_GLTF_TEXTURE_CONCURRENCY = 16

function normalizeTextureConcurrency(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_GLTF_LOADING_OPTIONS.texture.maxConcurrentDecodes
  return Math.min(MAX_GLTF_TEXTURE_CONCURRENCY, Math.max(1, Math.floor(value)))
}

/** 可用于日志、遥测和持久化诊断的稳定低内存原因码。 */
export const GLTF_LOW_MEMORY_REASON_CODES = [
  'standard-requested',
  'auto-not-beneficial',
  'invalid-manifest',
  'invalid-reference',
  'unsupported-extension',
  'unsupported-skin',
  'unsupported-morph-target',
  'unsupported-sparse-accessor',
  'unsupported-primitive-mode',
  'unsupported-material-extension',
  'unsupported-attribute',
  'budget-final-exceeded',
  'budget-transient-exceeded',
  'budget-batch-exceeded',
  'budget-range-cache-exceeded',
  'budget-texture-exceeded',
  'full-buffer-budget-exceeded',
  'range-unavailable',
  'range-invalid-response',
  'direct-build-failed',
  'cancelled',
] as const satisfies readonly GltfLowMemoryReasonCode[]

/** 未配置预算不设容量上限；有限约束必须由调用方提供。 */
export const DEFAULT_GLTF_MEMORY_BUDGET: Readonly<GltfMemoryBudgetOptions> = {
  maxFinalGeometryBytes: Infinity,
  maxTransientBytes: Infinity,
  maxMergedBatchBytes: Infinity,
  maxRangeCacheBytes: Infinity,
  maxTextureBytes: Infinity,
  maxPreflightBytes: Infinity,
}

/** 默认保持原有 Three.js GLTFLoader 行为的 glTF 配置。 */
export const DEFAULT_GLTF_LOADING_OPTIONS: Readonly<GltfLoadingOptions> = {
  mode: 'standard',
  unsupportedMode: 'fallback',
  range: {
    enabled: true,
    mergeGapBytes: 4 * 1024,
    maxRequestBytes: 8 * MEBIBYTE,
    maxConcurrentRequests: 4,
    fallbackToFullBuffer: true,
  },
  taskYield: {
    maxBytesPerTask: 4 * MEBIBYTE,
    maxVerticesPerTask: 100_000,
  },
  texture: {
    maxSize: 512,
    maxConcurrentDecodes: 4,
    autoSize: {
      candidateSizes: [256, 512, 1024],
      resourceUsageRatio: 1,
    },
  },
  budget: DEFAULT_GLTF_MEMORY_BUDGET,
}

/** 合并默认、插件级和请求级 glTF 配置。 */
export function resolveGltfLoadingOptions(
  pluginOptions?: GltfLoadingOptionsInput,
  requestOptions?: GltfLoadingOptionsInput
): GltfLoadingOptions {
  const resolved: GltfLoadingOptions = {
    ...DEFAULT_GLTF_LOADING_OPTIONS,
    ...pluginOptions,
    ...requestOptions,
    range: {
      ...DEFAULT_GLTF_LOADING_OPTIONS.range,
      ...pluginOptions?.range,
      ...requestOptions?.range,
    },
    taskYield: {
      ...DEFAULT_GLTF_LOADING_OPTIONS.taskYield,
      ...pluginOptions?.taskYield,
      ...requestOptions?.taskYield,
    },
    texture: {
      ...DEFAULT_GLTF_LOADING_OPTIONS.texture,
      ...pluginOptions?.texture,
      ...requestOptions?.texture,
      autoSize: {
        ...DEFAULT_GLTF_LOADING_OPTIONS.texture.autoSize,
        ...pluginOptions?.texture?.autoSize,
        ...requestOptions?.texture?.autoSize,
      },
    },
    budget: {
      ...DEFAULT_GLTF_LOADING_OPTIONS.budget,
      ...pluginOptions?.budget,
      ...requestOptions?.budget,
    },
  }
  resolved.texture.maxConcurrentDecodes = normalizeTextureConcurrency(
    resolved.texture.maxConcurrentDecodes
  )
  return resolved
}
